import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SaleExtras, extrasTotal, gbp2 } from "./SaleExtras";
import { useExtraPresets } from "./UsualExtras";

export const EMPTY_INVOICE = {
  sale_date: new Date().toISOString().slice(0, 10), buyer_name: "", buyer_address: "", payment_method: "Bank transfer",
  price: "", paid_in_full: true, vehicle_id: "", vehicle_model: "", registration: "", vin: "", engine_no: "", colour: "",
  mileage: "", conditions: "", other_notes: "", vat_scheme: "margin", vat_number: "", sale_no: "", extras: [],
};

const SECTIONS = [
  ["Sale", [["sale_date", "Date of sale", "date"], ["price", "Vehicle price (£)", "number"], ["payment_method", "Payment method", "select", ["Bank transfer", "Card", "Cash", "Cheque", "Finance"]]]],
  ["Buyer", [["buyer_name", "Buyer name"], ["buyer_address", "Buyer address (one line per row)", "textarea"]]],
  ["Vehicle", [["vehicle_model", "Make & model"], ["registration", "Registration"], ["vin", "VIN"], ["engine_no", "Engine no."], ["colour", "Colour"], ["mileage", "Mileage"]]],
  ["Terms", [["conditions", "Conditions of sale", "textarea"], ["other_notes", "Other notes", "textarea"]]],
  ["VAT", [["vat_scheme", "VAT treatment", "select", [["margin", "Margin scheme (VAT not shown)"], ["standard", "VAT qualifying (20% shown)"], ["none", "No VAT"]]], ["vat_number", "VAT registration no."]]],
];

const fromVehicle = (v) => ({
  vehicle_id: v.id, vehicle_model: [v.make, v.model, v.derivative].filter(Boolean).join(" "), registration: v.registration_number || "",
  vin: v.vin || "", colour: v.colour || "", mileage: v.mileage != null ? `${Number(v.mileage).toLocaleString("en-GB")} miles` : "", price: v.price ?? "",
});

export const Field = ({ k, label, type, options, value, onChange, prefix = "invoice" }) => {
  const props = { id: `${prefix}-${k}`, "data-testid": `${prefix}-field-${k}`, value: value ?? "", onChange: (e) => onChange(e.target.value), className: "field !py-2.5 text-sm" };
  return (
    <div className={type === "textarea" ? "sm:col-span-2" : ""}>
      <label htmlFor={props.id} className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">{label}</label>
      {type === "textarea" ? <textarea rows={3} {...props} /> : type === "select" ? (
        <select {...props}>{options.map((o) => { const [val, l] = Array.isArray(o) ? o : [o, o]; return <option key={val} value={val}>{l}</option>; })}</select>
      ) : <input type={type || "text"} step={type === "number" ? "0.01" : undefined} {...props} />}
    </div>
  );
};

export function InvoiceForm({ initial, invoiceNo, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useExtraPresets();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    api.get("/admin/vehicles").then((r) => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => setVehicles([]));
  }, []);
  const total = (Number(f.price) || 0) + extrasTotal(f.extras);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.buyer_name.trim() || !f.vehicle_model.trim() || !(Number(f.price) > 0)) return toast.error("Buyer name, make & model and a sale price are required");
    if (f.extras.some((x) => !x.description.trim())) return toast.error("Each extra needs a description");
    setBusy(true);
    try {
      const body = { ...f, price: Number(f.price), extras: f.extras.map((x) => ({ description: x.description.trim(), value: Number(x.value) || 0, vat: Number(x.vat) || 0, funds_received: !!x.funds_received })) };
      const { data } = invoiceNo ? await api.put(`/admin/invoices/${invoiceNo}`, body) : await api.post("/admin/invoices", body);
      toast.success(`Invoice ${data.invoice_no} saved`);
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border border-stone bg-paper p-6" data-testid="invoice-form">
      <h2 className="mb-5 font-serif text-2xl">{invoiceNo ? `Edit invoice ${invoiceNo}` : "New invoice"}</h2>
      <label htmlFor="inv-vehicle-pick" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Fill vehicle details from stock (optional)</label>
      <select id="inv-vehicle-pick" className="field mb-6 !py-2.5 text-sm" data-testid="invoice-vehicle-pick" value={f.vehicle_id}
        onChange={(e) => { const v = vehicles.find((x) => x.id === e.target.value); setF((p) => (v ? { ...p, ...fromVehicle(v) } : { ...p, vehicle_id: "" })); }}>
        <option value="">Not in stock list / enter manually</option>
        {vehicles.map((v) => <option key={v.id} value={v.id}>{[v.year, v.make, v.model, v.derivative].filter(Boolean).join(" ") + (v.status === "sold" ? " (sold)" : "")}</option>)}
      </select>
      {SECTIONS.map(([title, fields]) => (
        <div key={title}>
          <fieldset className="mb-6">
            <legend className="mb-3 text-sm font-semibold text-forest">{title}</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.filter(([k]) => !(k === "vat_number" && f.vat_scheme === "none")).map(([k, l, t, o]) => <Field key={k} k={k} label={l} type={t} options={o} value={f[k]} onChange={set(k)} />)}
            </div>
          </fieldset>
          {title === "Sale" && <SaleExtras extras={f.extras} onChange={set("extras")} presets={presets} setPresets={setPresets} />}
        </div>
      ))}
      {f.extras.length > 0 && Number(f.price) > 0 && <p className="mb-6 border border-stone bg-stone-light p-4 text-sm" data-testid="invoice-total">Vehicle {gbp2(f.price)} · extras {gbp2(extrasTotal(f.extras))} · <strong>invoice total {gbp2(total)}</strong></p>}
      <label className="mb-6 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.paid_in_full} onChange={(e) => set("paid_in_full")(e.target.checked)} data-testid="invoice-field-paid_in_full" />Paid in full (balance due £0.00)
      </label>
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={busy} className="btn btn-primary !py-3" data-testid="invoice-save">{busy ? "Saving…" : invoiceNo ? "Save changes" : "Create invoice"}</button>
        <button type="button" onClick={onCancel} className="btn !py-3" data-testid="invoice-cancel">Cancel</button>
      </div>
    </form>
  );
}
